import { FetchBuckets } from 'components/Fetch/FetchBuckets'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router'
import { getBucketById } from 'services/post/actions'
import { bucketsSelector } from 'services/post/selectors'
import { StoreThunkDispatch } from 'store/state'
import { BucketsHome } from './BucketsHome'
import BucketCreatePopup from './components/BucketCreatePopup'
import BucketPostsView from './components/BucketPostsView'

type BucketTemplate = { name: string, description: string, color: string }

export function BucketsRoute() {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [loading, setLoading] = useState(true)
  const buckets = useSelector(bucketsSelector)
  const [createPopupOpen, setCreatePopupOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<BucketTemplate | undefined>(undefined)

  useEffect(() => {
    if (location.state?.showCreateBucket) {
      openCreateBucketPopup(location.state.showCreateBucket)
      navigate(location.pathname, { replace: true })
    }
  }, [location.pathname, location.state?.showCreateBucket, navigate])

  useEffect(() => {
    if (selectedTemplate) {
      setCreatePopupOpen(true)
    }
  }, [buckets.length, loading, selectedTemplate])

  useEffect(() => {
    if (params.id) {
      dispatch(getBucketById(params.id)).unwrap()
    }
  }, [dispatch, params.id])

  const stopLoading = useCallback(() => {
    setLoading(false)
  }, [])

  const activeBucket = useMemo(() => {
    return buckets.find(b => b.id === params.id)
  }, [params.id, buckets])

  useEffect(() => {
    if (activeBucket && (activeBucket.id !== params.id || !params.type)) {
      navigate(`/posts/buckets/${activeBucket.id}/${params.type || 'posts'}`)
    }
  }, [activeBucket, params.id, params.type, navigate])

  const onCreatePopupClose = () => {
    if (buckets.length === 0) {
      navigate('/posts/planned/all')
    } else {
      setCreatePopupOpen(false)
      setSelectedTemplate(undefined)
    }
  }

  const openCreateBucketPopup = (template: BucketTemplate) => {
    setSelectedTemplate(template)
  }

  return (
    <div>
      {!activeBucket && (
        <BucketsHome onRequestCreateBucket={openCreateBucketPopup} />
      )}
      <FetchBuckets onFetched={stopLoading} onError={stopLoading} />

      <BucketCreatePopup
        open={createPopupOpen}
        init={selectedTemplate}
        onClose={onCreatePopupClose}
        onCreated={onCreatePopupClose}
      />
      {!loading && activeBucket && (
        <BucketPostsView
          activeView={params.type === 'posts' ? 'planned' : params.type as any}
          bucket={activeBucket}
        />
      )}
    </div>
  )
}

export default BucketsRoute
